import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BlockingMessageDialog } from "../BlockingMessageDialog";
import {
  WarningOctagon, CaretRight, CaretDown, CheckCircle, ArrowsClockwise, Info,
} from "@phosphor-icons/react";
import { fmtMoney } from "../../lib/format";
import { humanise } from "../../lib/enumLabels";
import { CalcLevelGateCta } from "../CalcLevelGateCta";
import { RcRuleSummary } from "./RcRuleSummary";
import { ReviewRowLine, ReviewRowHeader, RefundSummary } from "./ReviewRowLine";
import { bulkAckReason } from "./bulkAckReason";
import { MissingRateAction } from "./MissingRateAction";
import { GroupAnswers } from "./GroupAnswers";   // A2-582
import { RowAnswers } from "./RowAnswers";       // A2-582
import { rowActionRank, HAS_PER_ROW_ACTION_MIN } from "./reviewCanonical";
import { groupAnswer, isNote } from "./answers";
import { useRowBoxes, forgetRowBoxes } from "./useRowBoxes";   // A1-105 (a)
import { CardSummary, amountsText } from "./CardSummary";   // A1-105 five-part card
import { ROWS_PAGE } from "./moreRows";
import { revealRow, revealPendingFor } from "./revealRow";   // A2-581 lead 21
import { cardInstruction } from "./cardInstruction";   // A2-637 Owner B

// v0.6 P2 refactor — one review-group card extracted from
// GroupedReviewQueue.jsx (500-line cap). Behaviour unchanged; the
// per-group state/handlers are passed in as props.
/**
 * v0.4.19 UI1 — Grouped review queue with bulk-acknowledge.
 *
 * Replaces the flat 4,485-row ReviewQueue with a flag-typed group
 * view per operator spec (sign-off 2026-02-15):
 *  - Family A (informational): bulk-ack button + shared reason field.
 *  - Family B (decision-required): no bulk-ack; per-row decisions
 *    handled by ViesHeldQueue / supersede flows.
 *  - Replayed rows: own muted-teal group (Q1-d=b).
 *  - Q1-c: Family B expanded by default, Family A collapsed.
 *  - Q1-e: 50 rows per page (backend page_size default).
 */

  const statusOf = (g, ackedAll) => {
    if (ackedAll || g.family === "REPLAYED") return "reviewed";
    if (isNote(g)) return "audit";   // A1-103: a note is never "to do"
    if (g.tier === "must" || (g.family === "B" && !g.off_period_only)) return "blocking";   // A2-640
    // the rest is "worth checking": a Family A card whose answer can change a
    // figure (api/_review_taxonomy.tier_of).
    return "needs-decision";
  };
  // A2-579 (operator 2026-10-27, "like Las Vegas"): neutral cards; colour
  // only marks what stops filing, as a thin edge and small text — no filled badges.
  const statusStyle = {
    blocking: "border-orange-600/70 dark:border-orange-400/70 bg-white dark:bg-slate-900",
    "needs-decision": "border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900",
    audit: "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900",
    reviewed: "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 opacity-70",
  };
  // A2-598 (operator 2026-10-28): an OPENED card and the panel under it read
  // as one piece — a faint wash of the card's own colour and a thicker edge,
  // never bright (A2-579, "like Las Vegas").
  const openStyle = {
    blocking: "border-l-4 border-orange-600/60 dark:border-orange-400/50 bg-orange-50/50 dark:bg-orange-950/20",
    "needs-decision": "border-l-4 border-amber-600/45 dark:border-amber-400/40 bg-amber-50/40 dark:bg-amber-950/15",
    audit: "border-l-4 border-slate-300 dark:border-slate-600 bg-slate-50/70 dark:bg-slate-800/30",
    reviewed: "border-l-4 border-slate-300 dark:border-slate-600 bg-slate-50/70 dark:bg-slate-800/30 opacity-70",
  };
  const panelStyle = {
    blocking: "border-orange-600/20 dark:border-orange-400/20",
    "needs-decision": "border-amber-600/20 dark:border-amber-400/20",
    audit: "border-slate-200 dark:border-slate-700",
    reviewed: "border-slate-200 dark:border-slate-700",
  };
  const statusPill = {
    blocking: "text-orange-700 dark:text-orange-300",
    "needs-decision": "text-amber-700 dark:text-amber-300/90",
    audit: "text-slate-500 dark:text-slate-400",
    reviewed: "text-slate-500 dark:text-slate-400",
  };

/**
 * v0.6.10 — the one place a flag is explained in full.
 *
 * Rendered by the (i) toggle in the header and again at the top of the
 * expanded body, so the operator gets the same words whichever way they
 * asked for them. Nothing here is ever shown unasked: on a queue of
 * twenty groups, twenty always-on explanations are noise, and noise is
 * what made the genuinely blocking groups hard to find.
 */
const FlagExplainer = ({ g, t, showCode = false }) => {
  // v0.6.10 — many flags now carry no body at all: the title says what
  // happened and the action says what to do, so a third sentence would
  // only repeat one of them. The old fallback printed "No description
  // for this flag — check the audit log", which is an apology where
  // nothing was missing. Render nothing instead.
  const description = t(`flags.${g.flag_type}`, { defaultValue: "" });
  return (
  <>
    {/* v0.6.10 — max-w-[70ch] is the readability fix that matters most
        here. These ran the full width of a 2000px window, so the eye
        had to track ~200 characters back to the next line. Everything
        else (sans prose, muted description against a stronger action,
        a rule between them) is hierarchy: what happened, then what to
        do about it. */}
    {description && (
      <div className="text-[12px] max-w-[70ch] text-slate-600 dark:text-slate-300 leading-relaxed">
        {description}
      </div>
    )}
    <div
      data-testid={`rg-action-${g.flag_type}`}
      className="text-[12px] max-w-[70ch] flex items-start gap-1.5 text-slate-900 dark:text-slate-100 leading-relaxed border-l-2 border-slate-200 dark:border-slate-700 pl-2.5"
    >
      <span className="font-semibold shrink-0">
        {t("ui.review_grouped.what_to_do", { defaultValue: "What to do:" })}
      </span>
      <span>
        {cardInstruction(g, t)}
      </span>
    </div>
    {/* v0.6.12 — the engine code now appears ONLY when the operator asks
        for it with the (i), never on a plain expand.

        v0.6.10 had already decided this code "is a developer identifier,
        not something the operator decides on" and moved it out of the
        heading INTO this explainer. Until v0.6.12 the explainer ALSO
        rendered automatically when a group was opened, so the code was
        still on screen every time an operator opened a group, which on
        the review page is constantly. The operator sent screenshots of
        six of them. That automatic render is gone too (operator,
        2026-10-02): the (i) is now the only way to see any of this.

        Clicking (i) IS an explicit request for developer detail, so the
        code stays reachable in one click and never appears unbidden. */}
    {showCode && (
      <code
        data-testid={`rg-code-${g.flag_type}`}
        className="inline-block text-[9px] tabular-nums px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700"
        title={t("ui.review_grouped.internal_code")}
      >
        {g.flag_type}
      </code>
    )}
  </>
  );
};

// v0.6.10 — Family B rows can be acknowledged together with one
// note. See FamilyBBulkResolver for why that satisfies the
// backend's per-row rule rather than bypassing it.
// v0.6.12 — "Upload date range narrower than filing period" as the dates
// themselves. Operator, 2026-10-11: the card listed one row and Inspect
// opened one transaction — "how does it show you that the date range is
// narrower than filing period?" It couldn't; this sentence does.
// ISO dates, as the rest of the app (lib/format.fmtDate, the trace drawer).
const _day = (iso) => String(iso || "").slice(0, 10);
const _days = (a, b) => Math.round((new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / 86400000);
export const periodSentence = (n, t) => {
  const lead = Math.max(0, _days(n.period_start, n.data_first));
  const trail = Math.max(0, _days(n.data_last, n.period_end));
  const gaps = [
    lead > 0 ? t("ui.review_grouped.period_gap_start", { n: lead, defaultValue: "the first {{n}} days" }) : null,
    trail > 0 ? t("ui.review_grouped.period_gap_end", { n: trail, defaultValue: "the last {{n}} days" }) : null,
    // Roadmap 4.1 — a run of empty days inside the period (a missing month).
    ...(n.gaps || []).map((g) => t("ui.review_grouped.period_gap_middle", {
      from: _day(g.from), to: _day(g.to), defaultValue: "{{from}} to {{to}}" })),
  ].filter(Boolean).join(t("ui.review_grouped.period_gap_and", { defaultValue: " and " }));
  return t("ui.review_grouped.period_sentence", {
    first: _day(n.data_first), last: _day(n.data_last),
    start: _day(n.period_start), end: _day(n.period_end), gaps,
    defaultValue: "Your upload runs from {{first}} to {{last}}, but the filing period is {{start}} to {{end}} — {{gaps}} have no transactions. Check no file is missing, then record a reason below.",
  });
};

// A1-105 (card inventory 2026-11-01): a card over several currencies said only
// "multiple currencies". It names its largest ones, each in its own currency
// (never one sum under one label), and how many more there are.
export const cardAmounts = (g, t) => ((g.amounts || []).length
  ? amountsText(g.amounts, t)
  : t("ui.review_grouped.multiple_currencies", { defaultValue: "multiple currencies" }));

export const ReviewGroupCard = ({
  g, calcId, clientId, calcFrozen, highlightSection, onInspect,
  open, setOpen, draftReason, setDraftReason, busy, ackGroup,
  refresh, wf, data, canonicalMap, onMoreRows,
}) => {
  const { t } = useTranslation();
  const [showFrozenDialog, setShowFrozenDialog] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  // A1-74 — audit-trail and inherited cards keep their rows behind a toggle.
  const [showRows, setShowRows] = useState(() => revealPendingFor(g.flag_type)
    || (!(isNote(g) || g.family === "REPLAYED" || g.period_note) && !((g.count || 0) > 10)));   // A2-579: >10 rows (or a notes card) open folded
  useEffect(() => {   // A2-581 lead 21: another card's "dealt with under" link
    const on = (e) => { if (e.detail?.flag === g.flag_type) setShowRows(true); };
    window.addEventListener("rg-reveal-row", on);
    return () => window.removeEventListener("rg-reveal-row", on);
  }, [g.flag_type]);
  // v0.6.10 — Family B bulk selection. Nothing is pre-selected: the operator names
  // the rows they are deciding on, which is exactly what routes_review_ack's Family B rule requires.
  const [bulkSel, setBulkSel] = useState([]);
  const [moreBusy, setMoreBusy] = useState(false);   // A1-105: the card's next page
  // A1-105 (a) — the return boxes each shown row fills
  const rowBoxes = useRowBoxes(calcId, g.calc_level ? [] : g.txn_ids, showRows);
            const ackedAll = g.acknowledged_count >= g.count;
            // v0.5.0i+7 — reviewed groups stay collapsed by default.
            // Operator can still expand them via the chevron for an
            // audit-look at the underlying rows, but they no longer
            // render a heavy expanded body on first paint. Needs-
            // action groups remain expanded by default (Family B
            // has individual decisions to record; Family A shows
            // the bulk-ack composer).
            // Audit-trail-only groups also start collapsed: there is no
            // decision inside to act on, so expanding them by default
            // pushed the groups that DO need one below the fold.
            const isOpen = open[g.flag_type] === undefined
              ? (!ackedAll && !isNote(g))
              : !!open[g.flag_type];
            const isFamilyB = g.family === "B";
            // A2-209 / A1-60 — the establishment question is answered on
            // whichever card the row sits on; A2-582: the server sends the
            // answer on exactly the rows its route accepts (row.answers), so
            // nothing here decides that. The reverse-charge rule per country
            // is shown wherever the rows carry one.
            const isEstablishment = (g.rows || []).some((r) => r.rc_rule);
            const onDone = async (freshCalcId) => {
              // v0.6.7 — a resolver that recalculated hands back the NEW
              // calc id; refetching the superseded one would overwrite the
              // correct post-recalc state.
              // Only a calc id counts: the rate pick handed back its whole
              // reply, and the queue refetched /runs/calc/[object Object]
              // (independent review, 2026-10-28).
              const cid = (typeof freshCalcId === "string" && freshCalcId) || calcId;
              forgetRowBoxes();   // an answer can move a row without a new calculation
              await refresh(cid);
              await wf?.refreshReview?.(cid);
            };
            const isReplayed = g.family === "REPLAYED";
            const status = statusOf(g, ackedAll);
            const highlighted = highlightSection
              && (g.section_counts || {})[highlightSection] > 0;
  return (
            <>
              <li
                key={g.flag_type}
                data-testid={`rg-group-${g.flag_type}`}
                data-highlighted={highlighted ? "true" : undefined}
                data-state={status}
                data-open={isOpen ? "true" : undefined}
                className={`${isOpen ? openStyle[status] : `border-l-2 ${statusStyle[status]}`} ${
                  highlighted ? "ring-1 ring-teal-400/60" : ""
                }`}
              >
                <div className="flex items-start">
                <button
                  type="button"
                  data-testid={`rg-toggle-${g.flag_type}`}
                  onClick={() => setOpen((s) => ({ ...s, [g.flag_type]: !isOpen }))}
                  className="flex-1 min-w-0 text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                >
                  {isOpen ? <CaretDown size={14} className="mt-1" weight="bold" /> : <CaretRight size={14} className="mt-1" weight="bold" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm flex items-center gap-2 flex-wrap">
                      {/* v0.5.6 UX Phase 1 (item 5) — status pill */}
                      <span
                        data-testid={`rg-status-${g.flag_type}`}
                        className={`text-xs font-semibold uppercase tracking-wide ${statusPill[status]}`}
                      >
                        {t(`ui.review_grouped.status_${status.replace("-", "_")}`, {
                          defaultValue: status === "blocking"
                            ? "Blocking"
                            : status === "needs-decision"
                              ? "Needs decision"
                              : "Reviewed",
                        })}
                      </span>
                      <span
                        data-testid={`rg-title-${g.flag_type}`}
                        className={`font-semibold ${
                          ackedAll || isReplayed
                            ? "text-slate-500 dark:text-slate-400"
                            : "text-slate-900 dark:text-slate-100"
                        }`}
                      >
                        {isReplayed
                          ? t("ui.review_grouped.replayed_group_title")
                          : t(`flag_titles.${g.flag_type}`,
                              { defaultValue: humanise(g.flag_type) })}
                      </span>
                      {/* v0.6.10 — the raw enum badge moved into the
                          (i) explainer. It is a developer identifier,
                          not something the operator decides on, and on
                          a long queue it doubled the width of every
                          heading for no operator benefit. */}
                      {/* A1-105 (operator 2026-11-01, a Q1 Swedish run): the period card is
                          about the whole upload; its one row only carries the note
                          (engine/period_boundary), so no row count or amount here */}
                      {!g.period_note && (<>
                      <span
                        data-testid={`rg-count-${g.flag_type}`}
                        className="bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full text-[10px] tabular-nums"
                      >
                        {g.count}
                      </span>
                      <span
                        data-testid={`rg-total-${g.flag_type}`}
                        className="text-[11px] text-slate-600 dark:text-slate-300"
                      >
                        {(g.currencies || []).length > 1
                          ? cardAmounts(g, t)
                          : `${fmtMoney(g.total_gross)} ${g.currency}`}
                      </span>
                      </>)}
                      {groupAnswer(g, "GO_TO", { target: "step2.fx" }) && (g.currencies?.length > 0) && (
                        <span
                          data-testid="rg-missing-fx-currencies"
                          className="text-[10px] tabular-nums px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200"
                          title={t("ui.review_grouped.missing_fx_currencies_title", { defaultValue: "Currencies needing a period exchange rate" })}
                        >
                          {g.currencies.join(", ")}
                        </span>
                      )}
                      {ackedAll && (
                        <span className="flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-300">
                          <CheckCircle size={12} weight="bold" />
                          {t("ui.review_grouped.acknowledged")}
                        </span>
                      )}
                      {!ackedAll && g.acknowledged_count > 0 && (
                        <span className="text-[11px] text-emerald-700 dark:text-emerald-300">
                          {t("ui.review_grouped.partially_acked", { acked: g.acknowledged_count, total: g.count })}
                        </span>
                      )}
                    </div>
                    {/* v0.6.10 — the description + "What to do" pair used
                        to render here, on EVERY group, collapsed or not.
                        Twenty groups meant twenty paragraphs stacked
                        before the operator reached a single control, and
                        the text that mattered (the group they were
                        working) looked identical to the nineteen they
                        were not. Both now live in the (i) explainer and
                        at the head of the expanded body — one click
                        away, never competing for the scan. */}
                  </div>
                </button>
                {!isReplayed && (
                  <button
                    type="button"
                    data-testid={`rg-info-${g.flag_type}`}
                    aria-expanded={showInfo}
                    aria-label={t("ui.review_grouped.explain_flag")}
                    title={t("ui.review_grouped.explain_flag")}
                    onClick={() => setShowInfo((v) => !v)}
                    className={`shrink-0 mt-3 mr-3 p-1.5 rounded-full border transition-colors ${
                      showInfo
                        ? "border-teal-500 bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300"
                        : "border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100"
                    }`}
                  >
                    <Info size={12} weight="bold" />
                  </button>
                )}
                </div>

                {showInfo && !isReplayed && (
                  <div
                    data-testid={`rg-info-panel-${g.flag_type}`}
                    className="mx-4 mb-3 px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 space-y-2"
                  >
                    <FlagExplainer g={g} t={t} showCode />
                  </div>
                )}


                {/* The exchange-rate jump names the missing pair and the box that
                    clears it; shown open or folded (A2-582: where the server's
                    answer is the rate in Step 2). */}
                {!ackedAll && groupAnswer(g, "GO_TO", { target: "step2.fx" }) && (
                  <MissingRateAction flagType={g.flag_type} group={g} />
                )}

                {isOpen && (
                  // v0.6.10 — the monospace utility removed from this container.
                  // index.css states the rule it was breaking: ".monospace-utility
                  // used only on numbers (money, percentages, IDs,
                  // hashes)". Every flag explanation rendered as
                  // monospace prose across the full window width,
                  // which is about the hardest combination to read
                  // there is. Values that SHOULD be monospaced now say so
                  // individually.
                  <div data-testid={`rg-body-${g.flag_type}`} className={`mx-4 mb-3 px-4 py-3 rounded-md border bg-white/70 dark:bg-slate-900/50 ${panelStyle[status]} text-[11px] text-slate-600 dark:text-slate-300 space-y-2`}>
                    {/* v0.6.10 had the explainer lead the body on every
                        expand ("opening a group is itself a request for
                        detail"). v0.6.12 (operator, 2026-10-02): "the UI
                        should be as clean as possible — put the text behind
                        the (i), click to see the full text." So the (i)
                        above is now the ONLY place the description and
                        "What to do" render; an expanded group shows its
                        title, chips and rows and nothing it was not asked
                        for. */}
                    {/* v0.6 — the per-card frozen note was removed: the
                        single top-level banner in GroupedReviewQueue already
                        states it. calcFrozen still hard-disables every action
                        below (unchanged). */}

                    {/* A2-582 (operator 2026-10-27: "nothing flipping works
                        here") — every control below is drawn from the answers
                        the server sent (g.bulk_answers, row.answers; see
                        answers.js), and the server accepts exactly those. The
                        card no longer chooses a control by its flag's name. */}
                    {/* A1-105: what happened, why it matters, what to do — on every card */}
                    {!isReplayed && !g.period_note && <CardSummary g={g} t={t} />}

                    {isFamilyB && !g.calc_level && !(g.bulk_answers || []).length && (
                      <div className="text-slate-700 dark:text-slate-200 flex items-center gap-2">
                        <WarningOctagon size={12} weight="bold" className="text-orange-600 dark:text-orange-300" />
                        {t("ui.review_grouped.family_b_notice")}
                      </div>
                    )}

                    {g.calc_level && (
                      <div className="text-red-700 dark:text-red-300 flex items-start gap-2">
                        <WarningOctagon size={12} weight="bold" className="mt-0.5 shrink-0" />
                        <div>
                          <div>{t("ui.review_grouped.calc_level_notice")}</div>
                          <CalcLevelGateCta />
                        </div>
                      </div>
                    )}

                    {/* Roadmap 1b.8 (A1-62) — each country's own reverse-charge
                        rule, once per country. */}
                    {isEstablishment && <RcRuleSummary rows={g.rows} />}

                    {/* A2-578: the group's answers ABOVE the rows, the tick bar sticky at the top. */}
                    {!g.calc_level && (
                      <GroupAnswers g={g} calcId={calcId} clientId={clientId} calcFrozen={calcFrozen}
                        data={data} bulkSel={bulkSel} setBulkSel={setBulkSel} setShowRows={setShowRows}
                        onDone={onDone} />
                    )}
                    {/* A1-74 (operator, 2026-10-11) — one line per row,
                        rows owned by another card folded into ONE summary
                        line per card, and audit-trail / inherited cards
                        reduced to a sentence with their rows on request.
                        See ReviewRowLine.jsx for the operator's words. */}
                    {(() => {
                        const rowsById = Object.fromEntries(
                          (g.rows || []).map((r) => [r.txn_id, r]));
                        // Rows whose full line lives on ANOTHER card, keyed
                        // by that card. One summary line each replaces the
                        // per-row "shown under ..." references, which on the
                        // operator's audit-trail card were 15 of its 17 lines.
                        const elsewhere = {};
                        const lines = (g.txn_ids || []).map((tid) => {
                          const rowAcked = (g.acknowledged_txn_ids || []).includes(tid);
                          const ctx = rowsById[tid];
                          const canon = canonicalMap?.canonicalOf?.(tid) || null;
                          const dupCount = canonicalMap?.countOf?.(tid) || 1;
                          const isDupNonCanon = dupCount > 1 && canon && canon.flag_type !== g.flag_type;
                          if (isDupNonCanon && rowActionRank(g) < HAS_PER_ROW_ACTION_MIN) {
                            (elsewhere[canon.flag_type] = elsewhere[canon.flag_type] || []).push(tid);
                            return null;
                          }
                          // The pairing explains a CREDIT NOTE's own card; on
                          // the inherited card it repeated once per row and
                          // answered nothing the card is about.
                          const refund = ctx?.refund_pairing && !isReplayed
                            ? <RefundSummary key={`${tid}-refund`} pairing={ctx.refund_pairing} tid={tid} />
                            : null;
                          return (
                            <React.Fragment key={tid}>
                              <ReviewRowLine tid={tid} anchorId={`rgrow-${g.flag_type}-${tid}`}
                                orderId={ctx?.order_id} gross={ctx?.gross} currency={ctx?.currency}
                                net={ctx?.net} vat={ctx?.vat} place={ctx?.place} boxes={rowBoxes[tid]}>
                                {/* A calc-level gate offers no per-row answer: its row
                                    is an arbitrary representative and a decision
                                    recorded on it cannot clear the gate. */}
                                <RowAnswers row={g.calc_level ? null : ctx} g={g} tid={tid} calcId={calcId}
                                  clientId={clientId} calcFrozen={calcFrozen} rowAcked={rowAcked}
                                  onInspect={onInspect} onDone={onDone} data={data}
                                  bulkSel={bulkSel} setBulkSel={setBulkSel} />
                              </ReviewRowLine>
                              {refund}
                            </React.Fragment>
                          );
                        });
                        const visible = lines.filter(Boolean);
                        // The label is built here, not inside the button: this
                        // is navigation, and the UX audit (test_v0501c8)
                        // reads a button's SOURCE for loaded words — the
                        // `flag` identifier matched "Flag". What renders
                        // ("N of these rows are dealt with under …") resolves
                        // nothing and claims nothing.
                        const elsewhereLines = Object.entries(elsewhere).map(([owner, tids]) => {
                          const ownerTitle = owner === "REPLAYED_FROM_PRIOR_CALC"
                            ? t("ui.review_grouped.replayed_group_title")
                            : t(`flag_titles.${owner}`, { defaultValue: humanise(owner) });
                          // A1-105 (operator 2026-10-30: "21 of 1486 on this page" vs
                          // "Showing 50 of 1486"): the count is of the rows SHOWN, and
                          // says so when the card has more rows than this page
                          const shownN = (g.txn_ids || []).length;
                          const label = g.count > shownN
                            ? t("ui.review_grouped.rows_elsewhere_paged", {
                                n: tids.length, shown: shownN, card: ownerTitle,
                                defaultValue: "{{n}} of the {{shown}} rows shown are dealt with under “{{card}}” →",
                              })
                            : t("ui.review_grouped.rows_elsewhere", {
                                n: tids.length,
                                card: ownerTitle,
                                defaultValue: "{{n}} of these rows are dealt with under “{{card}}” →",
                              });
                          return (
                          <button
                            key={`elsewhere-${owner}`}
                            type="button"
                            data-testid={`rg-elsewhere-${g.flag_type}-${owner}`}
                            onClick={() => {
                              // one card at a time (operator 2026-11-02): the
                              // card these rows belong to takes this one's place
                              setOpen({ [owner]: true });
                              revealRow(owner, tids[0]);
                            }}
                            className="self-start text-left text-xs py-0.5 text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100"
                          >
                            ⓘ {label}
                          </button>
                          );
                        });
                        // An audit-trail or inherited card has nothing to
                        // decide. It says what it is in one line; its own
                        // rows wait behind a toggle.
                        // v0.6.12 — the period-gap flag is a fact about the
                        // whole upload; its one row is only a carrier. The
                        // card states the dates instead of listing it.
                        const periodNote = g.period_note || null;
                        const sentenceOnly = isNote(g) || isReplayed || !!periodNote;
                        return (
                          <div data-testid={`rg-rows-${g.flag_type}`} className="mt-2 flex flex-col">
                            {sentenceOnly && (
                              <div data-testid={`rg-card-sentence-${g.flag_type}`} className="text-xs text-slate-600 dark:text-slate-300 pb-1">
                                {periodNote
                                  ? periodSentence(periodNote, t)
                                  : isReplayed
                                  ? t("ui.review_grouped.replayed_sentence", {
                                      defaultValue: "Already decided in the calculation this one replaced — that decision was reused. Nothing to do.",
                                    })
                                  : t("ui.review_grouped.audit_sentence", {
                                      defaultValue: "Recorded for the audit trail — nothing to do.",
                                    })}
                              </div>
                            )}
                            {/* A2-579 (operator 2026-10-27: "I don't want hundreds of
                                lines to scroll"): every group's rows fold; the answer
                                control above covers the whole group. */}
                            {visible.length > 0 && !periodNote && (   /* A1-105: the carrier row is no part of the finding */
                              <button
                                type="button"
                                data-testid={`rg-show-rows-${g.flag_type}`}
                                aria-expanded={showRows}
                                onClick={() => setShowRows((v) => !v)}
                                className="self-start text-xs py-1 text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100"
                              >
                                {/* A1-103 (operator 2026-10-31: these lines "are showing same
                                    way as those that need fixing"): a quiet toggle, not a task */}
                                <span aria-hidden="true">{showRows ? "▾ " : "▸ "}</span>
                                {showRows
                                  ? t("ui.review_grouped.hide_rows", { defaultValue: "Hide rows" })
                                  : t("ui.review_grouped.show_rows", { n: g.count, defaultValue: "Rows ({{n}})" })}
                              </button>
                            )}
                            {showRows && <><ReviewRowHeader />{visible}</>}
                            {/* information about the rows: only with the rows */}
                            {showRows && elsewhereLines}
                          </div>
                        );
                    })()}
                    {showRows && g.count > (g.txn_ids || []).length && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-3">
                        <span>{t("ui.review_grouped.paged_note", { shown: (g.txn_ids || []).length, total: g.count })}</span>
                        {onMoreRows && (
                          <button type="button" data-testid={`rg-more-rows-${g.flag_type}`}
                            disabled={!!moreBusy}
                            onClick={async () => {
                              setMoreBusy(true);
                              try { await onMoreRows(g.flag_type); } catch { /* the rows already shown stay */ }
                              setMoreBusy(false);
                            }}
                            className="underline underline-offset-2 text-slate-700 dark:text-slate-200 disabled:opacity-60">
                            {moreBusy
                              ? t("ui.review_grouped.more_rows_busy", { defaultValue: "Loading…" })
                              : t("ui.review_grouped.more_rows", {
                                  n: Math.min(ROWS_PAGE, g.count - (g.txn_ids || []).length),
                                  defaultValue: "Show the next {{n}} rows",
                                })}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Family A → bulk-ack composer. v0.5.0f+4 —
                        replaces the v0.5.0c ReasonPicker + preset
                        dropdown + <TOKEN> placeholder pattern with
                        a server-generated default reason rendered
                        read-only by default + optional edit
                        toggle. No more "fill in <DATE> before
                        saving" friction. */}
                    {g.bulk_ackable && !ackedAll && groupAnswer(g, "KEEP") && (() => {
                      const current = draftReason[g.flag_type];
                      const defaultText = bulkAckReason(
                        g,
                        t(`flag_titles.${g.flag_type}`,
                          { defaultValue: humanise(g.flag_type) }),
                      );
                      const value = current === undefined
                        ? defaultText : current;
                      const valid = (value || "").trim().length >= 3
                        && !/[<>]/.test(value || "");
                      return (
                        <div className="flex flex-wrap items-center gap-2 pt-1">
                          {/* A1-103 (operator 2026-10-31, "short form"): one line —
                              the automatic reason, shown in full on hover, and the
                              one button. The reason is still recorded and audited. */}
                          <div
                            data-testid={`rg-reason-readonly-${g.flag_type}`}
                            title={value}
                            className="flex-1 min-w-0 truncate text-[11px] text-slate-500 dark:text-slate-400"
                          >
                            {t("ui.review_grouped.reason_recorded_label", { defaultValue: "Recorded as" })}: {value}
                          </div>
                            <button
                              type="button"
                              data-testid={`rg-ack-${g.flag_type}`}
                              data-resolution-class="recordkeeping"
                              data-resolution-endpoint="POST /api/runs/calc/{calc_id}/review-acknowledge"
                              disabled={busy[g.flag_type] || !valid}
                              aria-disabled={calcFrozen}
                              onClick={() => {
                                // v0.6.8 — calcFrozen's reason used to be
                                // hover-only on a buried per-flag-group
                                // action button — high risk of being
                                // missed entirely (frozen calcs can't be
                                // re-acknowledged; this is a hard workflow
                                // rule, not a soft warning).
                                if (calcFrozen) { setShowFrozenDialog(true); return; }
                                ackGroup(g, value);
                              }}
                              title={calcFrozen
                                ? t("ui.review_grouped.ack_frozen_tooltip", { defaultValue: "Calc is frozen — acknowledgements are locked. To make changes, run a new calc and supersede this one." })
                                : undefined}
                              className="bg-amber-600 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed aria-disabled:opacity-40 aria-disabled:cursor-not-allowed text-white font-semibold text-[10px] px-3 py-2 rounded"
                            >
                              {busy[g.flag_type]
                                ? <ArrowsClockwise size={12} weight="bold" className="animate-spin" />
                                : g.period_note
                                  ? t("ui.review_grouped.period_confirm", { defaultValue: "Confirm no file is missing →" })
                                  : t("ui.review_grouped.acknowledge_all", { count: g.count })}
                            </button>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </li>
              <BlockingMessageDialog
                open={showFrozenDialog} testid={`rg-frozen-dialog-${g.flag_type}`} severity="warning"
                title={t("ui.review_grouped.frozen_dialog_title", { defaultValue: "Calc is frozen" })}
                onOpenChange={(o) => !o && setShowFrozenDialog(false)}
              ><p>{t("ui.review_grouped.frozen_dialog_body", { defaultValue: "Acknowledgements are locked. To make changes, run a new calc and supersede this one." })}</p></BlockingMessageDialog>
            </>
  );
};
